import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import ViewInvoiceDrawer from './ViewInvoiceDrawer';

const STATUS_COLORS = {
  pagado: 'bg-green-100 text-green-800',
  pendiente: 'bg-yellow-100 text-yellow-800',
  fallido: 'bg-red-100 text-red-800',
  cancelado: 'bg-gray-100 text-gray-800',
};

const OrdersTable = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = async () => {
    try {
      const res = await api.get('/api/orders');
      setOrders(res.data);
      setFilteredOrders(res.data);
    } catch (err) {
      console.error('Error al cargar órdenes:', err);
      setError('No se pudieron cargar las órdenes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    let result = orders;
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (o) =>
          o.invoice_code?.toLowerCase().includes(lowerSearch) ||
          o.user_name.toLowerCase().includes(lowerSearch) ||
          o.email.toLowerCase().includes(lowerSearch)
      );
    }
    if (statusFilter) {
      result = result.filter((o) => o.status === statusFilter);
    }
    setFilteredOrders(result);
  }, [searchTerm, statusFilter, orders]);

  const handleEdit = (order) => setSelectedOrder(order);
  const handleCloseDrawer = () => setSelectedOrder(null);

  const handleOrderUpdated = (updatedOrder) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === updatedOrder.id ? { ...o, status: updatedOrder.status } : o))
    );
    setFilteredOrders((prev) =>
      prev.map((o) => (o.id === updatedOrder.id ? { ...o, status: updatedOrder.status } : o))
    );
    setSelectedOrder(null);
  };

  return (
    <div className="bg-[oklch(97%_0.014_254.604)] min-h-screen py-4 px-4 sm:px-6 lg:px-8 font-[Manrope]">
      <div className="mx-auto max-w-7xl">
        <div className="bg-white shadow-md rounded-xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Órdenes</h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-600">
                Historial de pedidos realizados por los usuarios.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:gap-4 mb-6 gap-4">
            <input
              type="text"
              placeholder="Buscar por factura, nombre o correo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded px-2 py-2 text-sm w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="pagado">Pagado</option>
              <option value="fallido">Fallido</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-3 sm:px-4">Factura</th>
                  <th className="px-2 py-3 sm:px-4">Cliente</th>
                  <th className="px-2 py-3 sm:px-4 hidden sm:table-cell">Total</th>
                  <th className="px-2 py-3 sm:px-4">Estado</th>
                  <th className="px-2 py-3 sm:px-4 hidden md:table-cell">Fecha</th>
                  <th className="px-2 py-3 sm:px-4">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredOrders.map((o) => (
                  <tr key={o.id} className="hover:bg-gray-50">
                    <td className="px-2 py-3 sm:px-4 font-semibold text-blue-800 whitespace-nowrap">
                      {o.invoice_code || '-'}
                    </td>
                    <td className="px-2 py-3 sm:px-4">{o.user_name}</td>
                    <td className="px-2 py-3 sm:px-4 hidden sm:table-cell">
                      ${parseFloat(o.total).toFixed(2)}
                    </td>
                    <td className="px-2 py-3 sm:px-4">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${STATUS_COLORS[o.status] || ''}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-2 py-3 sm:px-4 hidden md:table-cell whitespace-nowrap">
                      {new Date(o.created_at).toISOString().split('T')[0]}
                    </td>
                    <td className="px-2 py-3 sm:px-4">
                      <button
                        className="text-blue-700 hover:underline text-xs sm:text-sm"
                        onClick={() => handleEdit(o)}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && filteredOrders.length === 0 && (
            <p className="text-xs sm:text-sm text-gray-500 text-center py-8">
              No hay resultados que coincidan.
            </p>
          )}
        </div>
      </div>

      {selectedOrder && (
        <ViewInvoiceDrawer
          order={selectedOrder}
          onClose={handleCloseDrawer}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
    </div>
  );
};

export default OrdersTable;